import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from './button.jsx';
import { Link } from '../navigation/link.jsx';

const ITEM_HEIGHT = 36;
const CONTAINER_PAD = 8;

/**
 * @param {DOMRect} buttonRect
 * @param {number} itemCount
 * @param {number} [measuredHeight]
 * @returns {{ top: number, right: number }}
 */
function getDropdownStyle(buttonRect, itemCount, measuredHeight) {
	const estHeight = measuredHeight ?? itemCount * ITEM_HEIGHT + CONTAINER_PAD;
	const vh = window.innerHeight;
	const spaceBelow = vh - buttonRect.bottom;
	const openUp = spaceBelow < estHeight;
	const top = openUp
		? buttonRect.top - (measuredHeight || estHeight) - 4
		: buttonRect.bottom + 4;
	const right = document.documentElement.clientWidth - buttonRect.right;
	return { top, right };
}

/**
 * @param {{ items: Array<{ label: string, onClick: () => void, to?: string, danger?: boolean }> }} props
 * @returns {JSX.Element | null}
 */
export function ActionMenu({ items }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const wrapRef = useRef(/** @type {HTMLDivElement|null} */ (null));
	const dropdownRef = useRef(/** @type {HTMLDivElement|null} */ (null));
	const buttonRectRef = useRef(/** @type {DOMRect|null} */ (null));
	const [style, setStyle] = useState(/** @type {{ top: number, right: number }|null} */ (null));

	const reposition = useCallback(() => {
		const rect = buttonRectRef.current;
		if (!rect) {return;}
		const height = dropdownRef.current ? dropdownRef.current.offsetHeight : undefined;
		setStyle(getDropdownStyle(rect, items.length, height));
	}, [items.length]);

	useEffect(() => {
		if (!open) {
			setStyle(null);
			return () => {};
		}
		reposition();
		window.addEventListener('scroll', reposition, { capture: true });
		window.addEventListener('resize', reposition);
		return () => {
			window.removeEventListener('scroll', reposition, { capture: true });
			window.removeEventListener('resize', reposition);
		};
	}, [open, reposition]);

	useEffect(() => {
		if (!open) {return () => {};}
		/** @param {MouseEvent} e */
		function handleOutside(e) {
			const target = /** @type {Node} */ (e.target);
			const outsideWrap = wrapRef.current && !wrapRef.current.contains(target);
			const outsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);
			if (outsideWrap && outsideDropdown) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleOutside);
		return () => document.removeEventListener('mousedown', handleOutside);
	}, [open]);

	if (items.length === 0) {return null;}

	return (
		<div className="action-menu-wrap" ref={wrapRef}>
			<Button
				size="icon"
				icon={<>&hellip;</>}
				onClick={() => {
					if (open) {
						setOpen(false);
					} else {
						buttonRectRef.current = wrapRef.current.getBoundingClientRect();
						setOpen(true);
					}
				}}
				aria-label={t('aria.actions')}
			/>
			{open && style && createPortal(
				<div
					className="action-menu-dropdown"
					ref={dropdownRef}
					style={{ position: 'fixed', top: style.top, right: style.right }}
				>
					{items.map((item, i) => {
						const cls = `action-menu-item${item.danger ? ' danger' : ''}`;
						if (item.to) {
							return (
								<Link
									key={i}
									to={item.to}
									className={cls}
									onClick={() => setOpen(false)}
								>
									{item.label}
								</Link>
							);
						}
						return (
							<Button
								key={i}
								variant="ghost"
								className={cls}
								onClick={() => { setOpen(false); item.onClick(); }}
							>
								{item.label}
							</Button>
						);
					})}
				</div>,
				document.body
			)}
		</div>
	);
}
